// ============================================================
// PLUMME - AI Advisor: month-specific report sections
// ============================================================
// A monthly report is a closed calendar-month view. It must never inherit the
// annual or quarterly selector, and it must not mix today's open items into a
// historical month. All comparisons here come from the scoped month context.
const PlummeAiAdvisorReportMonthly = (() => {
  const B = () => window.PlummeAiAdvisorReportBlocks;

  const monthCell = (ctx, year = ctx.year) => ({
    key: 'adv.rpt.period.monthShort', vars: { month: ctx.period.month, year },
  });

  const monthlyStatusSection = (ctx) => {
    const { num, round, round1, percentChange, deltaTone, section, note, chart } = B();
    const current = ctx.report || {};
    const previous = ctx.previousMonthReport || {};
    const income = num(current.inntekter);
    const costs = num(current.kostnader);
    const result = num(current.resultat);
    const margin = income > 0 ? round1(result / income * 100) : null;
    const previousIncome = num(previous.inntekter);
    const previousMargin = previousIncome > 0 ? round1(num(previous.resultat) / previousIncome * 100) : null;
    const compare = { key: 'adv.rpt.period.vsPreviousMonth' };
    const change = (value, before, betterWhen) => {
      const delta = percentChange(value, before);
      return delta == null ? {} : { delta, deltaTone: deltaTone(delta, betterWhen), compare };
    };
    return section('monthStatus', 'adv.rpt.sec.monthlyStatus', [
      {
        type: 'kpis',
        items: [
          { id: 'income', label: { key: 'adv.rpt.kpi.income' }, value: round(income), format: 'currency', ...change(income, previous.inntekter, 'high') },
          { id: 'costs', label: { key: 'adv.rpt.kpi.costs' }, value: round(costs), format: 'currency', ...change(costs, previous.kostnader, 'low') },
          { id: 'result', label: { key: 'adv.rpt.kpi.result' }, value: round(result), format: 'currency', ...change(result, previous.resultat, 'high') },
          { id: 'margin', label: { key: 'adv.rpt.kpi.margin' }, value: margin, format: 'percent',
            ...(margin == null || previousMargin == null ? {} : {
              delta: round1(margin - previousMargin),
              deltaTone: deltaTone(margin - previousMargin, 'high'), compare,
            }) },
        ],
      },
      chart('waterfall', {
        compact: true,
        titleKey: 'adv.rpt.chart.summaryResult',
        opening: round(income),
        openingLabel: { key: 'adv.rpt.kpi.income' },
        categories: [{ key: 'adv.rpt.kpi.costs' }],
        series: [{ id: 'costs', labelKey: 'adv.rpt.kpi.costs', values: [-round(costs)] }],
        closing: round(result),
        closingLabel: { key: 'adv.rpt.kpi.result' },
      }),
      note(result >= 0 ? 'success' : 'warning', result >= 0
        ? 'adv.rpt.monthly.statusProfit' : 'adv.rpt.monthly.statusLoss', {
        month: ctx.period.month, year: ctx.year, income: round(income), result: round(Math.abs(result)), margin,
      }),
    ]);
  };

  const monthlyBudgetSection = (ctx) => {
    const { num, round1, percentChange, section, note, empty, money, chart } = B();
    const plan = ctx.plan;
    if (!plan || !plan.hasBudget) {
      return section('monthBudget', 'adv.rpt.sec.monthlyBudget', [empty('adv.rpt.empty.noMonthlyBudget', {
        month: ctx.period.month, year: ctx.year,
      })]);
    }
    const totals = plan.totals || {};
    const budget = [num(totals.incomeBudgetToDate), num(totals.costBudgetToDate)];
    const actual = [num(totals.incomeActual), num(totals.costActual)];
    budget.push(budget[0] - budget[1]);
    actual.push(actual[0] - actual[1]);
    const variance = actual.map((value, index) => value - budget[index]);
    const variancePct = variance.map((_, index) => percentChange(actual[index], budget[index]));
    return section('monthBudget', 'adv.rpt.sec.monthlyBudget', [
      chart('bars', {
        compact: true,
        titleKey: 'adv.rpt.chart.monthBudgetActual',
        categories: [{ key: 'adv.rpt.row.income' }, { key: 'adv.rpt.row.costs' }, { key: 'adv.rpt.row.result' }],
        series: [
          { id: 'budget', labelKey: 'adv.rpt.col.budget', values: budget },
          { id: 'actual', labelKey: 'adv.rpt.col.actual', values: actual },
        ],
      }),
      {
        type: 'table',
        cols: [{ key: 'adv.rpt.col.line' }, { key: 'adv.rpt.col.budget' }, { key: 'adv.rpt.col.actual' }, { key: 'adv.rpt.col.variancePct' }],
        rows: ['income', 'costs', 'result'].map((id, index) => [
          { key: `adv.rpt.row.${id}` }, money(budget[index]), money(actual[index]),
          { value: variancePct[index] == null ? null : round1(variancePct[index]), format: 'percent',
            tone: variance[index] === 0 ? null : ((id === 'costs') === (variance[index] < 0) ? 'success' : 'danger') },
        ]),
      },
      note(variance[2] >= 0 ? 'success' : 'warning', variance[2] >= 0
        ? 'adv.rpt.monthly.budgetAbove' : 'adv.rpt.monthly.budgetBelow', { amount: Math.abs(variance[2]) }),
    ]);
  };

  const monthlyComparisonSection = (ctx) => {
    const { num, round1, percentChange, section, note, empty, money, chart } = B();
    if (!ctx.previousYearAvailable) {
      return section('monthComparison', 'adv.rpt.sec.monthlyComparison', [empty('adv.rpt.empty.noMonthlyComparison', {
        month: ctx.period.month, year: ctx.year - 1,
      })]);
    }
    const previous = ctx.previousReport || {};
    const current = ctx.report || {};
    const before = [num(previous.inntekter), num(previous.kostnader), num(previous.resultat)];
    const now = [num(current.inntekter), num(current.kostnader), num(current.resultat)];
    const changes = now.map((value, index) => percentChange(value, before[index]));
    const previousMargin = before[0] > 0 ? before[2] / before[0] * 100 : null;
    const currentMargin = now[0] > 0 ? now[2] / now[0] * 100 : null;
    return section('monthComparison', 'adv.rpt.sec.monthlyComparison', [
      chart('bars', {
        compact: true,
        titleKey: 'adv.rpt.chart.sameMonthComparison',
        categories: [{ key: 'adv.rpt.row.income' }, { key: 'adv.rpt.row.costs' }, { key: 'adv.rpt.row.result' }],
        series: [
          { id: 'previous', labelKey: 'adv.rpt.chart.previousPeriod', values: before },
          { id: 'current', labelKey: 'adv.rpt.chart.currentPeriod', values: now },
        ],
      }),
      {
        type: 'table',
        cols: [{ key: 'adv.rpt.col.line' }, monthCell(ctx, ctx.year - 1), monthCell(ctx), { key: 'adv.rpt.col.change' }],
        rows: ['income', 'costs', 'result'].map((id, index) => [
          { key: `adv.rpt.row.${id}` }, money(before[index]), money(now[index]),
          { value: changes[index] == null ? null : round1(changes[index]), format: 'percent',
            tone: changes[index] == null || changes[index] === 0 ? null : ((id === 'costs') === (changes[index] < 0) ? 'success' : 'danger') },
        ]),
      },
      previousMargin == null || currentMargin == null ? null : note('info', 'adv.rpt.monthly.marginChange', {
        change: round1(currentMargin - previousMargin),
      }),
    ]);
  };

  const monthlyTrendSection = (ctx) => {
    const { num, safeArray, round, round1, section, note, empty, chart, MONTH_KEYS } = B();
    const months = safeArray(ctx.monthlyTrend);
    if (!months.some((month) => num(month.inc) || num(month.cost))) {
      return section('monthTrend', 'adv.rpt.sec.monthlyTrend', [empty('adv.rpt.empty.noMonthlyTrend')]);
    }
    const categories = months.map((month) => ({ key: `adv.rpt.month.${MONTH_KEYS[month.monthIndex]}` }));
    const results = months.map((month) => round(num(month.inc) - num(month.cost)));
    const average = results.reduce((sum, value) => sum + value, 0) / results.length;
    return section('monthTrend', 'adv.rpt.sec.monthlyTrend', [
      {
        type: 'metrics',
        items: [
          { id: 'average', label: { key: 'adv.rpt.metric.averageMonthlyResult' }, value: round(average), format: 'currency' },
          { id: 'best', label: { key: 'adv.rpt.metric.bestMonthlyResult' }, value: Math.max(...results), format: 'currency', tone: 'success' },
          { id: 'weakest', label: { key: 'adv.rpt.metric.weakestMonthlyResult' }, value: Math.min(...results), format: 'currency', tone: Math.min(...results) < 0 ? 'danger' : null },
        ],
      },
      chart('bars', {
        titleKey: 'adv.rpt.chart.monthlyResult', colorBySign: true, categories,
        series: [{ id: 'result', labelKey: 'adv.rpt.col.result', values: results }],
      }),
      note('info', 'adv.rpt.monthly.trendBasis', { from: ctx.period.from, to: ctx.period.to, months: results.length }),
    ]);
  };

  const monthlyInsightsSection = (ctx) => {
    const { num, percentChange, section } = B();
    const report = ctx.report || {};
    const previous = ctx.previousReport || {};
    const totals = ctx.plan?.totals || {};
    const result = num(report.resultat);
    const incomeChange = ctx.previousYearAvailable ? percentChange(report.inntekter, previous.inntekter) : null;
    const costBudgetVariance = ctx.plan?.hasBudget ? num(totals.costActual) - num(totals.costBudgetToDate) : null;
    const resultBudget = num(totals.incomeBudgetToDate) - num(totals.costBudgetToDate);
    const resultBudgetVariance = ctx.plan?.hasBudget ? result - resultBudget : null;
    const findings = [];
    const steps = [];
    if (result < 0) {
      findings.push({ id: 'loss', severity: 'critical', title: { key: 'adv.rpt.monthly.insightLossTitle' }, body: { key: 'adv.rpt.monthly.insightLossBody', vars: { amount: Math.abs(result) } } });
      steps.push({ key: 'adv.rpt.monthly.stepReviewDrivers' });
    } else {
      findings.push({ id: 'profit', severity: 'info', title: { key: 'adv.rpt.monthly.insightProfitTitle' }, body: { key: 'adv.rpt.monthly.insightProfitBody', vars: { amount: result } } });
    }
    if (incomeChange != null && incomeChange < 0) {
      findings.push({ id: 'income', severity: 'watch', title: { key: 'adv.rpt.monthly.insightIncomeDownTitle' }, body: { key: 'adv.rpt.monthly.insightIncomeDownBody', vars: { change: Math.abs(incomeChange) } } });
      steps.push({ key: 'adv.rpt.monthly.stepRevenue' });
    }
    if (costBudgetVariance != null && costBudgetVariance > 0) {
      findings.push({ id: 'costs', severity: 'watch', title: { key: 'adv.rpt.monthly.insightCostsTitle' }, body: { key: 'adv.rpt.monthly.insightCostsBody', vars: { amount: costBudgetVariance } } });
      steps.push({ key: 'adv.rpt.monthly.stepCosts' });
    }
    if (resultBudgetVariance != null && resultBudgetVariance < 0) {
      findings.push({ id: 'budget', severity: 'info', title: { key: 'adv.rpt.monthly.insightBudgetTitle' }, body: { key: 'adv.rpt.monthly.insightBudgetBody', vars: { amount: Math.abs(resultBudgetVariance) } } });
      steps.push({ key: 'adv.rpt.monthly.stepBudget' });
    }
    if (!steps.length) steps.push({ key: 'adv.rpt.monthly.stepContinue' });
    return section('monthInsights', 'adv.rpt.sec.monthlyInsights', [
      { type: 'risks', items: findings.slice(0, 3) },
      { type: 'list', items: steps.slice(0, 3) },
    ]);
  };

  return {
    monthlyStatusSection,
    monthlyBudgetSection,
    monthlyComparisonSection,
    monthlyTrendSection,
    monthlyInsightsSection,
  };
})();

Object.assign(window, { PlummeAiAdvisorReportMonthly });
